'use strict';

import ClassLogger from 'ClassLogger';

/**
 * Inspired by https://codepen.io/shshaw/pen/vKzoLL
 */
class Countdown {
    /**
     * Returns the class name used by the ClassLogger.
     *
     * @returns {string}
     */
    getClassName () {
        return 'Countdown';
    }

    /**
     * Represents a clock that can display the time or a countdown.
     * @param {Date} countdown - The end time for the countdown. If not specified, the clock
     * will display the current time.
     * @param {function} callback - The function to call when the countdown is complete.
     */
    constructor (countdown, callback) {
        /** @type {console} */
        this.logger = ClassLogger(this, true); // set second parameter to false to disable logging

        // Set the countdown and callback properties
        countdown = countdown ? new Date(Date.parse(countdown)) : false;
        callback = callback || function () { };

        // Set the update function based on whether a countdown is specified
        const updateFn = countdown ? this.getTimeRemaining : this.getTime;

        // Create a div element for the clock
        this.el = document.createElement('div');
        this.el.className = 'flip-clock';

        // Create an object to hold the countdown trackers
        const trackers = {};
        const t = updateFn(countdown);
        let key;
        let timeinterval;

        // Create a CountdownTracker object for each time unit and add it to the clock element
        for (key in t) {
            if (key === 'Total') { continue; }
            trackers[key] = new CountdownTracker(key, t[key]);
            this.el.appendChild(trackers[key].el);
        }

        // Initialize the update counter
        let i = 0;

        /**
         * Updates the clock.
         */
        function updateClock () {
            // Request the next animation frame
            timeinterval = requestAnimationFrame(updateClock);

            // Throttle the update function to prevent it from updating too frequently
            if (i++ % 10) { return; }

            // Get the time remaining or current time
            const t = updateFn(countdown);
            // If the countdown is complete, call the callback function and stop the update function
            if (t.Total < 0) {
                cancelAnimationFrame(timeinterval);
                for (key in trackers) {
                    trackers[key].update(0);
                }
                try {
                    callback();
                } catch (error) {
                    this.logger.error('Cllback error', error);
                }
                return;
            }

            // Update each countdown tracker with the new time remaining or current time
            for (key in trackers) {
                trackers[key].update(t[key]);
            }
        }

        // Start the update function after a delay of 500ms
        setTimeout(updateClock, 500);
    }

    /**
     * Calculates the time remaining until a specified end time.
     * @param {Date} endtime - The end time.
     * @returns {Object} An object containing the total time remaining and the time remaining for each time unit.
     */
    getTimeRemaining (endtime) {
        const t = Date.parse(endtime) - Date.parse(new Date());
        return {
            Total: t,
            Tage: Math.floor(t / (1000 * 60 * 60 * 24)),
            Stunden: Math.floor((t / (1000 * 60 * 60)) % 24),
            Minuten: Math.floor((t / 1000 / 60) % 60),
            Sekunden: Math.floor((t / 1000) % 60),
        };
    }

    /**
     * Gets the current time.
     * @returns {Object} An object containing the current time and the time for each time unit.
     */
    getTime () {
        const t = new Date();
        return {
            Total: t,
            Stunden: t.getHours() % 12,
            Minuten: t.getMinutes(),
            Sekunden: t.getSeconds(),
        };
    }
}

/**
 * Represents a countdown tracker for a specific time unit.
 * @constructor
 * @param {string} label - The label for the time unit.
 * @param {number} value - The initial value for the time unit.
 */
class CountdownTracker {
    constructor (label, value) {
        // Create span element for the countdown tracker
        const el = document.createElement('span');

        el.classList.add('flip-clock__piece');
        el.classList.add('flip-clock__piece--' + label.toLowerCase());

        // Set inner HTML for the span element
        el.innerHTML = '<b class="flip-clock__card card"><b class="card__top"></b><b class="card__bottom"></b>' +
            '<b class="card__back"><b class="card__bottom"></b></b></b>' +
            '<span class="flip-clock__slot">' + label + '</span>';

        // Set the span element as a property of the CountdownTracker object
        this.el = el;

        // Get the top, bottom, back, and backBottom elements of the span element
        const top = el.querySelector('.card__top');
        const bottom = el.querySelector('.card__bottom');
        const back = el.querySelector('.card__back');
        const backBottom = el.querySelector('.card__back .card__bottom');

        /**
         * Updates the value of the countdown tracker.
         * @param {number} val - The new value for the countdown tracker.
         */
        this.update = function (val) {
            // Format the value to have leading zero if necessary
            val = ('0' + val).slice(-2);
            // Only update the countdown tracker if the value has changed
            if (val !== this.currentValue) {
                // Set the data-value attribute of the back and bottom elements to the current value
                if (this.currentValue >= 0) {
                    back.setAttribute('data-value', this.currentValue);
                    bottom.setAttribute('data-value', this.currentValue);
                }
                // Set the current value to the new value
                this.currentValue = val;
                // Set the text of the top element to the new value
                top.innerText = this.currentValue;
                // Set the data-value attribute of the backBottom element to the new value
                backBottom.setAttribute('data-value', this.currentValue);

                // Add and remove the flip class to animate the countdown tracker
                this.el.classList.remove('flip');
                // eslint-disable-next-line
                void this.el.offsetWidth;
                this.el.classList.add('flip');
            }
        };

        // Update the countdown tracker with the initial value
        this.update(value);
    }
}

export default Countdown;
